import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { AreaChart, Area, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import {
	RiFileList3Line,
	RiCheckboxCircleLine,
	RiTruckLine,
	RiNotification3Line,
	RiArrowRightSLine,
	RiArrowRightLine,
	RiArrowUpLine,
	RiArrowDownLine,
	RiTimeLine,
} from "@remixicon/react";
import { useNews, useOilPrices, seriesStats, splitHeadline } from "../../hooks/useMarketData";

// Buyer counterpart of ordrx_frontend's SellerSidebar: clock + top headline,
// "Today's summary" counters, industry news carousel and WTI/Brent cards.
export default function BuyerSidebar({ summary }) {
	const navigate = useNavigate();
	const [now, setNow] = useState(new Date());
	const { news } = useNews();
	const { wti, brent } = useOilPrices("daily");

	useEffect(() => {
		const t = setInterval(() => setNow(new Date()), 1000);
		return () => clearInterval(t);
	}, []);

	const headline = news[0];
	const openNews = (url) => url && window.open(url, "_blank", "noopener,noreferrer");

	const stats = [
		{ Icon: RiFileList3Line, value: summary.bidRequests, label: "Active bid requests", to: "/orders", tab: "bidRequest" },
		{ Icon: RiCheckboxCircleLine, value: summary.openOrders, label: "Open orders", to: "/orders", tab: "openOrder" },
		{ Icon: RiTruckLine, value: summary.inProgress, label: "Deliveries in progress", to: "/orders", tab: "ordersInProgress" },
		{ Icon: RiNotification3Line, value: summary.unreadNotifications, label: "Unread notifications", to: "/notifications" },
	];

	return (
		<aside className="buyer-sidebar">
			<div className="side-card clock-card">
				<div className="clock-row">
					<span className="clock-icon">
						<RiTimeLine size={18} />
					</span>
					<div>
						<div className="clock-date">{moment(now).format("dddd, MMM D")}</div>
						<div className="clock-time">{moment(now).format("hh:mm:ss A")}</div>
					</div>
				</div>
				{headline ? (
					<button type="button" className="oil-headline" onClick={() => openNews(headline.newsUrl)}>
						{splitHeadline(headline).headline}
					</button>
				) : null}
			</div>

			<div className="side-group">
				<div className="side-title">Today's summary</div>
				<div className="stat-list">
					{stats.map(({ Icon, value, label, to, tab }) => (
						<button type="button" className="stat-row" key={label} onClick={() => navigate(to, tab ? { state: { tab } } : undefined)}>
							<span className="stat-icon">
								<Icon size={18} />
							</span>
							<span className="stat-num">{summary.loading ? "–" : value ?? 0}</span>
							<span className="stat-label">{label}</span>
							<span className="stat-chev">
								<RiArrowRightSLine size={16} />
							</span>
						</button>
					))}
				</div>
			</div>

			<div className="side-card news-card">
				<div className="news-head">
					<div className="side-title small">Market updates</div>
					<Link to="/market-updates" className="link-accent d-flex align-items-center gap-1">
						View more <RiArrowRightLine size={12} />
					</Link>
				</div>
				{news.length === 0 ? (
					<div className="text-muted small px-3">No headlines right now.</div>
				) : (
					<div className="news-scroll">
						{news.slice(0, 8).map((item, i) => {
							const { headline: title, source } = splitHeadline(item);
							return (
								<button type="button" className="news-item" key={item.newsUrl || i} onClick={() => openNews(item.newsUrl)}>
									<span className={`news-thumb thumb-${i % 3}`} />
									<span className="news-copy">
										<span className="news-title">{title}</span>
										{source ? <span className="news-meta">{source}</span> : null}
									</span>
								</button>
							);
						})}
					</div>
				)}
			</div>

			<div className="crude-grid">
				<CrudeCard series={wti} placeholder="WTI Crude" />
				<CrudeCard series={brent} placeholder="Brent Crude" />
			</div>
		</aside>
	);
}

export function CrudeCard({ series, placeholder }) {
	const { last, change, pct } = seriesStats(series);
	const up = change >= 0;
	const gradientId = `crude-${(series?.name || "x").replace(/\W+/g, "-")}`;
	return (
		<Link to="/market-updates" className="crude-card">
			<div className="crude-name">{series?.name || placeholder || "Crude"}</div>
			<div className="crude-price">{last != null ? `$${last.toFixed(2)}` : "–"}</div>
			{last != null ? (
				<div className={`crude-change ${up ? "up" : "down"}`}>
					{up ? <RiArrowUpLine size={12} /> : <RiArrowDownLine size={12} />}
					{Math.abs(change).toFixed(2)} ({Math.abs(pct).toFixed(2)}%)
				</div>
			) : (
				<div className="crude-change text-muted">Loading…</div>
			)}
			<div className="crude-chart">
				<ResponsiveContainer width="100%" height={48}>
					<AreaChart data={series?.points || []} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
						<defs>
							<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
								<stop offset="0%" stopColor="#4c6b0d" stopOpacity={0.25} />
								<stop offset="100%" stopColor="#4c6b0d" stopOpacity={0} />
							</linearGradient>
						</defs>
						<YAxis hide domain={["dataMin", "dataMax"]} />
						<Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, series?.name]} labelFormatter={(_, p) => p?.[0]?.payload?.date || ""} />
						<Area type="monotone" dataKey="value" stroke="#4c6b0d" strokeWidth={1.5} fill={`url(#${gradientId})`} />
					</AreaChart>
				</ResponsiveContainer>
			</div>
			{series && !series.live ? <div className="crude-sample">Sample data</div> : null}
		</Link>
	);
}
